import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import {
    GENRE_TITLE_LIMIT,
    NEW_ROW,
    TAG_TAGS_LIMIT,
    TAG_TEXT_LIMIT,
} from "../LandingPage/constants";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { Check } from "@mui/icons-material";
import DeleteModal from "../../UI/atoms/Popup/DeleteModal";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../utils/permissions";

/** Temporarily hide sub-genre Roku columns; keep data load/save wired. */
const SHOW_SUB_GENRE_ROKU_COLUMNS = false;
const DEFAULT_ROWS_PER_PAGE = 25;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const GenreTable = () => {
    // hooks
    const [loader, setLoader] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [genreList, setGenreList] = useState<any>([]);
    const [originalGenre, setOriginalGenre] = useState<any>(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [totalCount, setTotalCount] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [editableRowIndex, setEditableRowIndex] = useState<any>([]);
    const [loadingRow, setLoadingRow] = useState<any>([]);
    const [selectedGenre, setSelectedGenre] = useState<any>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.genre, ACTION.EDIT);
    const fetchRequestIdRef = useRef(0);

    useEffect(() => {
        getGenreRecords(page, rowsPerPage, search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, search]);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            const nextSearch = searchInput.trim();
            setSearch((prev) => {
                if (prev === nextSearch) return prev;
                setPage(0);
                return nextSearch;
            });
        }, 300);
        return () => window.clearTimeout(handle);
    }, [searchInput]);

    const resetTableMeta = () => {
        setErrors({});
    };
    const resetEditableState = () => {
        setEditableRowIndex([]);
        setLoadingRow([]);
    };

    const getGenreRecords = async (
        pageNumber = page,
        pageSize = rowsPerPage,
        searchValue = search
    ) => {
        const requestId = ++fetchRequestIdRef.current;
        resetTableMeta();
        setLoader(true);
        try {
            const pages = await getGenres(
                pageSize,
                pageNumber * pageSize,
                searchValue
            );
            if (requestId !== fetchRequestIdRef.current) return;
            if (pages?.list) {
                setGenreList([...pages.list]);
                setTotalCount(Number(pages.totalCount) || 0);
            } else {
                setGenreList([]);
                setTotalCount(0);
            }
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                setLoader(false);
            }
        }
    };

    const getGenres = async (
        limit: number | string = rowsPerPage,
        skip: number | string = 0,
        searchValue = search
    ) => {
        resetEditableState();
        const pages = await getGenreAPI(limit, skip, searchValue);
        if (pages && Array.isArray(pages.list)) {
            pages.list = pages.list.map((item: any) => {
                const mapped = { ...item };
                if (Array.isArray(item?.subGenresWithCodes)) {
                    const titles = item.subGenresWithCodes.map(
                        (g: any) => g?.title ?? ""
                    );
                    const codes = item.subGenresWithCodes.map(
                        (g: any) => g?.code ?? ""
                    );
                    mapped.subGenres = titles.join(",");
                    mapped.subGenreCodes = codes.join(",");
                }
                mapped.rokuCoreTags =
                    typeof item?.rokuCoreTags === "string"
                        ? item.rokuCoreTags
                        : Array.isArray(item?.rokuCoreTags)
                          ? item.rokuCoreTags.join(",")
                          : "";
                mapped.rokuNicheTags =
                    typeof item?.rokuNicheTags === "string"
                        ? item.rokuNicheTags
                        : Array.isArray(item?.rokuNicheTags)
                          ? item.rokuNicheTags.join(",")
                          : "";
                mapped.subGenreRokuCoreTags = Array.isArray(
                    item?.subGenreRokuCoreTags
                )
                    ? item.subGenreRokuCoreTags.map((v: any) =>
                          String(v ?? "")
                      )
                    : [];
                mapped.subGenreRokuNicheTags = Array.isArray(
                    item?.subGenreRokuNicheTags
                )
                    ? item.subGenreRokuNicheTags.map((v: any) =>
                          String(v ?? "")
                      )
                    : [];
                return mapped;
            });
        }
        return pages;
    };

    const getGenreAPI = async (
        limit: number | string,
        skip: number | string = 0,
        searchValue = ""
    ) => {
        try {
            const params = new URLSearchParams();
            params.set("skip", String(Number(skip) || 0));
            params.set("limit", String(limit));
            if (searchValue) params.set("search", searchValue);
            const saveVersion = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/genre?${params.toString()}`
            );
            setOriginalGenre(JSON.stringify(saveVersion.data?.data?.list));
            return saveVersion.data?.data;
        } catch (error: any) {
            console.log("Error in getting master information", error);
        }
    };

    const tableRowRender = () => {
        return (
            <TableRow>
                <TableCell width={150}>
                    <h3>Genre</h3>
                </TableCell>
                <TableCell width={180}>
                    <h3>Amazon Genre Code</h3>
                </TableCell>
                <TableCell width={220}>
                    <h3>Roku Genre Code</h3>
                </TableCell>
                <TableCell width={360}>
                    <h3>Definition</h3>
                </TableCell>
                {SHOW_SUB_GENRE_ROKU_COLUMNS && (
                    <>
                        <TableCell width={240}>
                            <h3>Sub Genre Roku Core</h3>
                            <div
                                style={{
                                    fontSize: "0.75rem",
                                    color: "#666",
                                    fontWeight: "normal",
                                    marginTop: "4px",
                                }}
                            >
                                aligned with Sub Genres
                            </div>
                        </TableCell>
                        <TableCell width={240}>
                            <h3>Sub Genre Roku Niche</h3>
                            <div
                                style={{
                                    fontSize: "0.75rem",
                                    color: "#666",
                                    fontWeight: "normal",
                                    marginTop: "4px",
                                }}
                            >
                                aligned with Sub Genres
                            </div>
                        </TableCell>
                    </>
                )}
                {canEdit && (
                    <TableCell width={120} align="center">
                        <h3>Actions</h3>
                    </TableCell>
                )}
            </TableRow>
        );
    };

    const handleRefresh = () => {
        getGenreRecords(page, rowsPerPage, search);
    };

    const handleEditRow = (index: any, event?: React.MouseEvent) => {
        if (event) {
            event.preventDefault();
        }
        // Store current scroll position
        const scrollPosition = window.scrollY || window.pageYOffset;
        addEditableRowIndex(index);
        // Restore scroll position after state update
        setTimeout(() => {
            window.scrollTo(0, scrollPosition);
        }, 0);
    };

    // When user clicks on the X icon for undo
    const handleCloseRow = (index: number, id: any) => {
        removeEditableRowIndex(index);
        const list = [...genreList];
        const currentgenre = JSON.parse(originalGenre).filter(
            (genre: any) => genre.genreId === list[index].genreId
        );
        list[index] = currentgenre[0];
        setGenreList(list);
        removeValidationError(id);
    };

    // add editable row setting for a particular row
    const addEditableRowIndex = (index: number) => {
        if (!editableRowIndex.includes(index)) {
            setEditableRowIndex([...editableRowIndex, index]);
        }
    };

    // Remove editable row setting for a particular row
    const removeEditableRowIndex = (index: number) => {
        const updatedEditableRow = editableRowIndex.filter(
            (number: any) => number !== index
        );
        setEditableRowIndex(updatedEditableRow);
    };

    const addLoadingRowIndex = (index: number) => {
        if (!loadingRow.includes(index)) {
            setLoadingRow([...loadingRow, index]);
        }
    };

    const removeLoadingRowIndex = (index: number) => {
        const updatedLoadingRow = loadingRow.filter(
            (number: any) => number !== index
        );
        setLoadingRow(updatedLoadingRow);
    };

    // When user types sub genre & clicks enter
    const handleAddSubGenre = (id: string, subGenre: string) => {
        const subGenresplit = subGenre.split(",");
        let rowArray = [...genreList];
        let subGenres = [];
        let rowValue = rowArray.filter((row: any) => row.genreId === id)[0];
        const index = rowArray.findIndex((item) => item.genreId === id);
        const mainGenreTitle = trimSpaces(rowValue.genreTitle) || "";
        subGenres = (rowValue.subGenres && rowValue.subGenres.split(",")) || [];
        const subGenreCodes =
            (rowValue.subGenreCodes && rowValue.subGenreCodes.split(",")) || [];
        for (const subGenreChip of subGenresplit) {
            let chip = String(subGenreChip).trim();
            let codePart = "";

            // Handle code if present (format: "Title:Code")
            if (chip.includes(":")) {
                const [titlePart, code] = chip.split(":");
                chip = titlePart.trim();
                codePart = code.trim() || "";
            }

            // Remove main genre prefix if already present to avoid duplication
            // Handle cases like "Drama - Family" or "Drama - Drama - Family"
            if (mainGenreTitle) {
                const prefix = `${mainGenreTitle} - `;
                // Remove all occurrences of the prefix from the beginning
                while (chip.startsWith(prefix)) {
                    chip = chip.substring(prefix.length).trim();
                }
            }

            // Add main genre prefix in dash format
            const formattedSubGenre =
                mainGenreTitle && chip ? `${mainGenreTitle} - ${chip}` : chip;

            subGenres.push(formattedSubGenre);
            subGenreCodes.push(codePart);
        }
        rowArray[index].subGenres = subGenres.join(",");
        rowArray[index].subGenreCodes = subGenreCodes.join(",");
        const subRokuCore = Array.isArray(rowArray[index].subGenreRokuCoreTags)
            ? [...rowArray[index].subGenreRokuCoreTags]
            : [];
        const subRokuNiche = Array.isArray(rowArray[index].subGenreRokuNicheTags)
            ? [...rowArray[index].subGenreRokuNicheTags]
            : [];
        while (subRokuCore.length < subGenres.length) subRokuCore.push("");
        while (subRokuNiche.length < subGenres.length) subRokuNiche.push("");
        rowArray[index].subGenreRokuCoreTags = subRokuCore;
        rowArray[index].subGenreRokuNicheTags = subRokuNiche;
        setGenreList(rowArray);
    };

    // When user changes the genre code in any row
    const handleUpdateGenreCode = (index: number, genreCode: string) => {
        if (genreCode.length > TAG_TEXT_LIMIT)
            genreCode = genreCode.slice(0, TAG_TEXT_LIMIT);
        let rowArray = [...genreList];
        rowArray[index].genreCode = genreCode;
        setGenreList(rowArray);
    };

    // When user changes the genre title in any row
    const handleUpdateGenreTitle = (index: number, genreTitle: string) => {
        if (genreTitle.length > TAG_TEXT_LIMIT)
            genreTitle = genreTitle.slice(0, TAG_TEXT_LIMIT);
        let rowArray = [...genreList];
        rowArray[index].genreTitle = genreTitle;
        setGenreList(rowArray);
        validationCheckGenreTitle(genreTitle, rowArray[index].genreId);
    };

    const handleUpdateDefinition = (index: number, definition: string) => {
        const rowArray = [...genreList];
        rowArray[index].definition = definition;
        setGenreList(rowArray);
    };

    const handleDeleteSubGenre = (
        index: number,
        chipIndex: number,
        subGenre: string
    ) => {
        let rowArray = [...genreList];
        const subGenres = [...rowArray[index].subGenres.split(",")];
        subGenres.splice(chipIndex, 1);
        const removedSubGenre = subGenres.filter(
            (value: any) => value !== subGenre
        );
        rowArray[index].subGenres = removedSubGenre.join(",");
        // remove matching sub genre code at same index
        const subGenreCodes = rowArray[index].subGenreCodes
            ? rowArray[index].subGenreCodes.split(",")
            : [];
        if (subGenreCodes.length) {
            subGenreCodes.splice(chipIndex, 1);
            rowArray[index].subGenreCodes = subGenreCodes.join(",");
        }
        const subRokuCore = Array.isArray(rowArray[index].subGenreRokuCoreTags)
            ? [...rowArray[index].subGenreRokuCoreTags]
            : [];
        const subRokuNiche = Array.isArray(rowArray[index].subGenreRokuNicheTags)
            ? [...rowArray[index].subGenreRokuNicheTags]
            : [];
        if (subRokuCore.length) subRokuCore.splice(chipIndex, 1);
        if (subRokuNiche.length) subRokuNiche.splice(chipIndex, 1);
        rowArray[index].subGenreRokuCoreTags = subRokuCore;
        rowArray[index].subGenreRokuNicheTags = subRokuNiche;
        setGenreList(rowArray);
    };

    // When user edits a sub genre code field
    const handleUpdateSubGenreCode = (
        rowIndex: number,
        subIndex: number,
        code: string
    ) => {
        let rowArray = [...genreList];
        const codes = rowArray[rowIndex].subGenreCodes
            ? rowArray[rowIndex].subGenreCodes.split(",")
            : [];
        const titles = rowArray[rowIndex].subGenres
            ? rowArray[rowIndex].subGenres.split(",")
            : [];
        while (codes.length < titles.length) codes.push("");
        codes[subIndex] = trimSpaces(code) || "";
        rowArray[rowIndex].subGenreCodes = codes.join(",");
        setGenreList(rowArray);
    };

    const handleUpdateRokuCsvField = (
        index: number,
        field: "rokuCoreTags" | "rokuNicheTags",
        tags: string[]
    ) => {
        const rowArray = [...genreList];
        rowArray[index][field] = tags.join(",");
        setGenreList(rowArray);
    };

    const handleAddRokuCsvTag = (
        index: number,
        field: "rokuCoreTags" | "rokuNicheTags",
        tag: string
    ) => {
        const value = trimSpaces(tag);
        if (!value) return;
        const rowArray = [...genreList];
        const existing =
            typeof rowArray[index][field] === "string" && rowArray[index][field]
                ? rowArray[index][field].split(",").map((t: string) => t.trim()).filter(Boolean)
                : [];
        if (existing.length >= TAG_TAGS_LIMIT) {
            enqueueSnackbar(`Maximum ${TAG_TAGS_LIMIT} tags allowed`, {
                variant: "warning",
            });
            return;
        }
        existing.push(value.substring(0, GENRE_TITLE_LIMIT));
        rowArray[index][field] = existing.join(",");
        setGenreList(rowArray);
    };

    const handleUpdateSubGenreRokuField = (
        rowIndex: number,
        subIndex: number,
        field: "subGenreRokuCoreTags" | "subGenreRokuNicheTags",
        value: string
    ) => {
        const rowArray = [...genreList];
        const titles = rowArray[rowIndex].subGenres
            ? rowArray[rowIndex].subGenres.split(",")
            : [];
        const list = Array.isArray(rowArray[rowIndex][field])
            ? [...rowArray[rowIndex][field]]
            : [];
        while (list.length < titles.length) list.push("");
        list[subIndex] = value;
        rowArray[rowIndex][field] = list;
        setGenreList(rowArray);
    };

    // Add row to the top of the table (for new genre save)
    const addEmptyRow = () => {
        const emptyRow = {
            genreTitle: "",
            tags: null,
            genreId: null,
            genreCode: "",
            subGenres: "",
            subGenreCodes: "",
            rokuCoreTags: "",
            rokuNicheTags: "",
            definition: "",
            subGenreRokuCoreTags: [] as string[],
            subGenreRokuNicheTags: [] as string[],
        };
        const list = [...genreList];
        if (list.length > 0 && list[0].genreId) {
            // can add empty row
            list.splice(0, 0, emptyRow);
            addEditableRowIndex(0);
        } else if (list.length == 0) {
            // can add empty row
            list.splice(0, 0, emptyRow);
            addEditableRowIndex(0);
        }
        setGenreList(list);
    };

    // To remove the empty row
    const removeEntryRow = (index: number) => {
        const list = [...genreList];
        removeEditableRowIndex(index);
        list.splice(index, 1);
        setGenreList(list);
        removeValidationError(NEW_ROW);
    };

    // On click of the save button
    // remove the index from editable row list & add loader
    const handleSaveGenre = async (
        index: number,
        row: any,
        isUpdate = false
    ) => {
        try {
            if (!trimSpaces(row?.genreTitle) || isPunctuationOnly(row?.genreTitle)) {
                validateCheckGenre(row, row?.genreId);
                return;
            }
            addLoadingRowIndex(index);
            const id = await genreSaveAPICall(row);
            if (!isUpdate) updateRowIndex(index, id);
            removeLoadingRowIndex(index);
            removeEditableRowIndex(index);
            await getGenreRecords(page, rowsPerPage, search);
        } catch (error) {
            console.log("error", error);
        }
    };

    // update index of row
    const updateRowIndex = (index: number, id: string) => {
        const list = [...genreList];
        list[index].genreId = id;
        setGenreList(list);
    };

    // On genre save/update API call
    const genreSaveAPICall = async (payload: any) => {
        const formatPayload = {
            genreId: payload.genreId || null,
            genreCode: trimSpaces(payload.genreCode) || null,
            genreTitle: trimSpaces(payload.genreTitle),
            tags: trimSpaces(payload.tags) || null,
            subGenres: trimSpaces(payload.subGenres) || null,
            subGenreCodes: trimSpaces(payload.subGenreCodes) || null,
            rokuCoreTags: trimSpaces(payload.rokuCoreTags) || null,
            rokuNicheTags: trimSpaces(payload.rokuNicheTags) || null,
            definition: payload.definition || "",
            subGenreRokuCoreTags: Array.isArray(payload.subGenreRokuCoreTags)
                ? payload.subGenreRokuCoreTags
                : [],
            subGenreRokuNicheTags: Array.isArray(payload.subGenreRokuNicheTags)
                ? payload.subGenreRokuNicheTags
                : [],
        };
        try {
            const saveVersion = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/genre`,
                formatPayload
            );
            if (saveVersion?.data?.success) {
                enqueueSnackbar(saveVersion?.data?.message, {
                    variant: "success",
                });
                return saveVersion?.data?.data?.genreId;
            } else {
                enqueueSnackbar(saveVersion?.data?.message, {
                    variant: "error",
                });
                return null;
            }
        } catch (error: any) {
            console.log("Error in genreSaveAPICall", error.response);
            enqueueSnackbar(error.response?.data?.message, {
                variant: "warning",
            });
        }
    };

    // close the delete modal
    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    // Make delete API call on delete modal confirmation
    const handleDeleteGenre = () => {
        genreDeleteAPICall();
    };

    // Open delete popup
    const onClickDelete = (selectedRow: any) => {
        setSelectedGenre(selectedRow);
        setIsDeleteModalOpen(true);
    };

    // API call for deleting the genre & refreshing the table
    const genreDeleteAPICall = async () => {
        try {
            const saveVersion = await axios.delete(
                `${process.env.BASE_URL}api/v1/cms/genre?genreId=${selectedGenre?.genreId}`
            );
            if (saveVersion?.data?.success) {
                enqueueSnackbar(saveVersion?.data?.message, {
                    variant: "success",
                });
                getGenreRecords(page, rowsPerPage, search);
            } else {
                enqueueSnackbar(saveVersion?.data?.message, {
                    variant: "error",
                });
            }
        } catch (error: any) {
            console.log("Error in genre delete APICall", error.response);
            enqueueSnackbar(error.response?.data?.message, {
                variant: "warning",
            });
            return null;
        } finally {
            setIsDeleteModalOpen(false);
            setSelectedGenre(null);
        }
    };

    // set validation errors when save button is clicked
    // this should check if any of the inputs are filled & display errors
    const validateCheckGenre = (row: any, genreId: string) => {
        const id = genreId || NEW_ROW;
        const genreTitle = trimSpaces(row?.genreTitle);

        let validationErrors = { ...errors };
        if (!genreTitle) {
            validationErrors[id] = {
                genreTitle: true,
                genreTitleMessage: "Genre title is required",
            };
        } else if (isPunctuationOnly(genreTitle)) {
            validationErrors[id] = {
                genreTitle: true,
                genreTitleMessage: "Genre title is invalid",
            };
        }
        setErrors(validationErrors);
    };

    // check validation for the genre title on change event
    const validationCheckGenreTitle = (genreTitle: string, genreId: string) => {
        const id = genreId || NEW_ROW;
        let validationErrors = { ...errors };
        if (validationErrors[id]) {
            if (genreTitle) {
                validationErrors[id].genreTitle = false;
            } else {
                validationErrors[id].genreTitle = true;
            }
        } else {
            const error = {
                genreTitle: genreTitle ? false : true,
                tags: false,
            };
            validationErrors[id] = error;
        }
        setErrors(validationErrors);
    };

    // if close button is clicked, remove the error for that id
    const removeValidationError = (id: string) => {
        let validationErrors = { ...errors };
        delete validationErrors[id];
        setErrors(validationErrors);
    };

    const trimSpaces = (value: string) => {
        if (typeof value == "string") {
            return value.trim();
        }
        return value;
    };

    const isPunctuationOnly = (value: string) => {
        const trimmed = trimSpaces(value);
        return typeof trimmed === "string" && trimmed.length > 0 && /^[.,\s]+$/.test(trimmed);
    };

    return (
        <div style={{ width: "100%" }}>
            <div className="header-content-list">
                <div className="ingest-header-refresh">
                    <span className="title-change-logs">
                        Genre List & Mapping{" "}
                    </span>{" "}
                    <RefreshIcon onClick={handleRefresh} />
                </div>
                <TextField
                    size="small"
                    label="Search genres"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    sx={{ minWidth: 260, ml: 2 }}
                />
                {canEdit && (
                    <Button
                        disabled={!loadingRow || loadingRow.length}
                        className="max-content-width float-center ml-1"
                        variant="contained"
                        onClick={() => addEmptyRow()}
                    >
                        Add Genre
                    </Button>
                )}
            </div>
            {loader && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        my: 1,
                    }}
                >
                    <CircularProgress size={22} />
                    <Typography variant="body2">Loading genres…</Typography>
                </Box>
            )}
            <div className="mt-2" style={{ width: "100%", position: "relative" }}>
                {loader && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            bgcolor: "rgba(0, 0, 0, 0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 160,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <TableContainer
                    component={Paper}
                    sx={{ width: "100%", opacity: loader ? 0.5 : 1 }}
                >
                    <Table sx={{ width: "100%" }} aria-label="simple table">
                        <TableHead>{tableRowRender()}</TableHead>
                        <TableBody>
                            {genreList.map((row: any, index: number) => {
                                const isEditingRow =
                                    editableRowIndex.includes(index) ||
                                    row?.genreId == null;
                                const subGenresArray =
                                    typeof row?.subGenres === "string" &&
                                    row?.subGenres.length
                                        ? row.subGenres.split(",")
                                        : [];
                                const rokuCoreTagsArray =
                                    typeof row?.rokuCoreTags === "string" &&
                                    row.rokuCoreTags.length
                                        ? row.rokuCoreTags
                                              .split(",")
                                              .map((t: string) => t.trim())
                                              .filter(Boolean)
                                        : [];
                                const subGenreRokuCoreArray = Array.isArray(
                                    row?.subGenreRokuCoreTags
                                )
                                    ? row.subGenreRokuCoreTags
                                    : [];
                                const subGenreRokuNicheArray = Array.isArray(
                                    row?.subGenreRokuNicheTags
                                )
                                    ? row.subGenreRokuNicheTags
                                    : [];
                                return (
                                    <TableRow
                                        className={
                                            (editableRowIndex.includes(index) ||
                                                row?.genreId == null) &&
                                            "h-100p"
                                        }
                                        key={row.genreId}
                                        sx={{
                                            "&:last-child td, &:last-child th":
                                                { border: 0 },
                                        }}
                                    >
                                        <TableCell>
                                            {editableRowIndex.includes(index) ||
                                            row?.genreId == null ? (
                                                <TextField
                                                    id="outlined-size-small"
                                                    value={row?.genreTitle}
                                                    className={
                                                        errors[
                                                            row?.genreId ||
                                                                NEW_ROW
                                                        ]?.genreTitle && "mt-1"
                                                    }
                                                    onChange={(e: any) =>
                                                        handleUpdateGenreTitle(
                                                            index,
                                                            e.target.value
                                                        )
                                                    }
                                                    size="small"
                                                    error={
                                                        errors[
                                                            row?.genreId ||
                                                                NEW_ROW
                                                        ]?.genreTitle || false
                                                    }
                                                    helperText={
                                                        errors[
                                                            row?.genreId ||
                                                                NEW_ROW
                                                        ]?.genreTitle
                                                            ? errors[
                                                                  row?.genreId ||
                                                                      NEW_ROW
                                                              ]?.genreTitleMessage ||
                                                              "Genre title is required"
                                                            : ""
                                                    }
                                                />
                                            ) : (
                                                <>{row?.genreTitle || ""}</>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editableRowIndex.includes(index) ||
                                            row?.genreId == null ? (
                                                <TextField
                                                    id="outlined-size-small"
                                                    value={row?.genreCode || ""}
                                                    onChange={(e: any) =>
                                                        handleUpdateGenreCode(
                                                            index,
                                                            e.target.value
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            ) : (
                                                <>{row?.genreCode || ""}</>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {row?.genreId &&
                                                !editableRowIndex.includes(
                                                    index
                                                ) &&
                                                rokuCoreTagsArray.map(
                                                    (
                                                        tag: string,
                                                        tagIndex: number
                                                    ) => (
                                                        <Chip
                                                            key={`roku-core-${row?.genreId}-${tagIndex}`}
                                                            className="mr-1 mb-1"
                                                            label={tag}
                                                        />
                                                    )
                                                )}
                                            {isEditingRow && (
                                                <Autocomplete
                                                    multiple
                                                    freeSolo
                                                    options={[]}
                                                    value={rokuCoreTagsArray}
                                                    onChange={(
                                                        _event,
                                                        newValue
                                                    ) => {
                                                        handleUpdateRokuCsvField(
                                                            index,
                                                            "rokuCoreTags",
                                                            newValue as string[]
                                                        );
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            placeholder="Hit Enter to add"
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                    "Enter"
                                                                ) {
                                                                    const input =
                                                                        e.target as HTMLInputElement;
                                                                    const value =
                                                                        input.value.trim();
                                                                    if (value) {
                                                                        handleAddRokuCsvTag(
                                                                            index,
                                                                            "rokuCoreTags",
                                                                            value
                                                                        );
                                                                        input.value =
                                                                            "";
                                                                        e.preventDefault();
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    sx={{
                                                        "& .MuiChip-root": {
                                                            backgroundColor:
                                                                "rgba(255, 255, 255, 0.15)",
                                                            color: "text.primary",
                                                        },
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 360 }}>
                                            {isEditingRow ? (
                                                <TextField
                                                    value={row?.definition || ""}
                                                    onChange={(e: any) =>
                                                        handleUpdateDefinition(
                                                            index,
                                                            e.target.value
                                                        )
                                                    }
                                                    size="small"
                                                    multiline
                                                    minRows={2}
                                                    fullWidth
                                                />
                                            ) : (
                                                <Tooltip title={row?.definition || ""}>
                                                    <span
                                                        style={{
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient:
                                                                "vertical",
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        {row?.definition || "-"}
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        {SHOW_SUB_GENRE_ROKU_COLUMNS && (
                                            <>
                                        <TableCell>
                                            {row?.genreId &&
                                                !editableRowIndex.includes(
                                                    index
                                                ) &&
                                                subGenresArray.map(
                                                    (
                                                        _sub: string,
                                                        subIdx: number
                                                    ) => {
                                                        const value = String(
                                                            subGenreRokuCoreArray[
                                                                subIdx
                                                            ] ?? ""
                                                        ).trim();
                                                        return (
                                                            <div
                                                                key={`sub-roku-core-view-${row?.genreId}-${subIdx}`}
                                                                style={{
                                                                    marginTop:
                                                                        subIdx ===
                                                                        0
                                                                            ? "0"
                                                                            : "8px",
                                                                    marginBottom:
                                                                        "0",
                                                                    height: "32px",
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                }}
                                                            >
                                                                {value ? (
                                                                    <Tooltip
                                                                        title={
                                                                            value
                                                                        }
                                                                    >
                                                                        <Chip
                                                                            className="mr-1"
                                                                            label={
                                                                                value
                                                                            }
                                                                            size="small"
                                                                            sx={{
                                                                                maxWidth:
                                                                                    "100%",
                                                                                "& .MuiChip-label":
                                                                                    {
                                                                                        overflow:
                                                                                            "hidden",
                                                                                        textOverflow:
                                                                                            "ellipsis",
                                                                                    },
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                ) : (
                                                                    <span
                                                                        style={{
                                                                            fontSize:
                                                                                "0.875rem",
                                                                            color: "#999",
                                                                            fontStyle:
                                                                                "italic",
                                                                        }}
                                                                    >
                                                                        —
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            {isEditingRow && (
                                                <>
                                                    <div
                                                        style={{
                                                            height: "72px",
                                                        }}
                                                    />
                                                    <div>
                                                        {subGenresArray.map(
                                                            (
                                                                _title: string,
                                                                subIdx: number
                                                            ) => (
                                                                <div
                                                                    key={`sub-roku-core-edit-${subIdx}`}
                                                                    style={{
                                                                        marginTop:
                                                                            subIdx ===
                                                                            0
                                                                                ? "0"
                                                                                : "8px",
                                                                        height: "40px",
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        padding:
                                                                            "4px 8px",
                                                                    }}
                                                                >
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        placeholder="core,tags"
                                                                        value={
                                                                            subGenreRokuCoreArray[
                                                                                subIdx
                                                                            ] ||
                                                                            ""
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleUpdateSubGenreRokuField(
                                                                                index,
                                                                                subIdx,
                                                                                "subGenreRokuCoreTags",
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            "& .MuiOutlinedInput-root":
                                                                                {
                                                                                    height: "32px",
                                                                                },
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {row?.genreId &&
                                                !editableRowIndex.includes(
                                                    index
                                                ) &&
                                                subGenresArray.map(
                                                    (
                                                        _sub: string,
                                                        subIdx: number
                                                    ) => {
                                                        const value = String(
                                                            subGenreRokuNicheArray[
                                                                subIdx
                                                            ] ?? ""
                                                        ).trim();
                                                        return (
                                                            <div
                                                                key={`sub-roku-niche-view-${row?.genreId}-${subIdx}`}
                                                                style={{
                                                                    marginTop:
                                                                        subIdx ===
                                                                        0
                                                                            ? "0"
                                                                            : "8px",
                                                                    marginBottom:
                                                                        "0",
                                                                    height: "32px",
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                }}
                                                            >
                                                                {value ? (
                                                                    <Tooltip
                                                                        title={
                                                                            value
                                                                        }
                                                                    >
                                                                        <Chip
                                                                            className="mr-1"
                                                                            label={
                                                                                value
                                                                            }
                                                                            size="small"
                                                                            sx={{
                                                                                maxWidth:
                                                                                    "100%",
                                                                                "& .MuiChip-label":
                                                                                    {
                                                                                        overflow:
                                                                                            "hidden",
                                                                                        textOverflow:
                                                                                            "ellipsis",
                                                                                    },
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                ) : (
                                                                    <span
                                                                        style={{
                                                                            fontSize:
                                                                                "0.875rem",
                                                                            color: "#999",
                                                                            fontStyle:
                                                                                "italic",
                                                                        }}
                                                                    >
                                                                        —
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            {isEditingRow && (
                                                <>
                                                    <div
                                                        style={{
                                                            height: "72px",
                                                        }}
                                                    />
                                                    <div>
                                                        {subGenresArray.map(
                                                            (
                                                                _title: string,
                                                                subIdx: number
                                                            ) => (
                                                                <div
                                                                    key={`sub-roku-niche-edit-${subIdx}`}
                                                                    style={{
                                                                        marginTop:
                                                                            subIdx ===
                                                                            0
                                                                                ? "0"
                                                                                : "8px",
                                                                        height: "40px",
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        padding:
                                                                            "4px 8px",
                                                                    }}
                                                                >
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        placeholder="niche,tags"
                                                                        value={
                                                                            subGenreRokuNicheArray[
                                                                                subIdx
                                                                            ] ||
                                                                            ""
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleUpdateSubGenreRokuField(
                                                                                index,
                                                                                subIdx,
                                                                                "subGenreRokuNicheTags",
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            "& .MuiOutlinedInput-root":
                                                                                {
                                                                                    height: "32px",
                                                                                },
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </TableCell>
                                            </>
                                        )}
                                        {canEdit && (
                                            <TableCell align="center">
                                                {
                                                    // default action buttons
                                                    row?.genreId &&
                                                        !row?.derivedFromGenreId &&
                                                        !editableRowIndex.includes(
                                                            index
                                                        ) && (
                                                            <>
                                                                <Tooltip title="Edit">
                                                                    <IconButton>
                                                                        <EditIcon
                                                                            onClick={(
                                                                                e
                                                                            ) =>
                                                                                handleEditRow(
                                                                                    index,
                                                                                    e
                                                                                )
                                                                            }
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Delete">
                                                                    <IconButton>
                                                                        <DeleteIcon
                                                                            onClick={() =>
                                                                                onClickDelete(
                                                                                    row
                                                                                )
                                                                            }
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )
                                                }
                                                {
                                                    // when editing the existing row in the table
                                                    row?.genreId &&
                                                        editableRowIndex.includes(
                                                            index
                                                        ) && (
                                                            <>
                                                                {(loadingRow.includes(
                                                                    index
                                                                ) && (
                                                                    <CircularProgress
                                                                        size={
                                                                            20
                                                                        }
                                                                    />
                                                                )) || (
                                                                    <Tooltip title="Save genre">
                                                                        <IconButton>
                                                                            <Check
                                                                                onClick={() =>
                                                                                    handleSaveGenre(
                                                                                        index,
                                                                                        row,
                                                                                        true
                                                                                    )
                                                                                }
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                <Tooltip title="Cancel">
                                                                    <IconButton>
                                                                        <CloseIcon
                                                                            onClick={() =>
                                                                                handleCloseRow(
                                                                                    index,
                                                                                    row.genreId
                                                                                )
                                                                            }
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )
                                                }
                                                {
                                                    // when adding new row in the table
                                                    row?.genreId == null && (
                                                        <>
                                                            {(loadingRow.includes(
                                                                index
                                                            ) && (
                                                                <CircularProgress
                                                                    size={20}
                                                                />
                                                            )) || (
                                                                <Tooltip title="Save genre">
                                                                    <IconButton>
                                                                        <Check
                                                                            onClick={() =>
                                                                                handleSaveGenre(
                                                                                    index,
                                                                                    row
                                                                                )
                                                                            }
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip title="Cancel">
                                                                <IconButton>
                                                                    <CloseIcon
                                                                        onClick={() =>
                                                                            removeEntryRow(
                                                                                index
                                                                            )
                                                                        }
                                                                    />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )
                                                }
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(_event, nextPage) => {
                            if (loader) return;
                            setPage(nextPage);
                        }}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            if (loader) return;
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    />
                </TableContainer>
            </div>
            <DeleteModal
                title="Delete Confirmation"
                isOpen={isDeleteModalOpen}
                handleClose={closeDeleteModal}
                handleConfirm={handleDeleteGenre}
            >
                <p>
                    Are you sure you want to delete the genre{" "}
                    <b>{selectedGenre?.genreTitle}</b>?
                </p>
                <p
                    style={{
                        marginTop: "12px",
                        fontSize: "0.875rem",
                        color: "#666",
                    }}
                >
                    <strong>Note:</strong> Deleting a genre will not remove it
                    from existing titles.
                </p>
            </DeleteModal>
        </div>
    );
};

export default GenreTable;
